import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  RoomAudioRenderer,
  useLocalParticipant,
  useParticipants,
  useRoomContext,
  useTracks
} from '@livekit/components-react'
import { RoomEvent, Track } from 'livekit-client'

import type { BlockedDevice } from './DevicePermissionDialog'
import type { DeviceWarning } from './deviceWarning'
import type { ECallConnectionQuality } from './useConnectionQuality'

import { DevicePermissionDialog } from './DevicePermissionDialog'
import { deviceWarning } from './deviceWarning'
import { refreshDeviceList } from './refreshDeviceList'
import {
  permissionWarning,
  queryDevicePermission,
  useDevicePermission
} from './useDevicePermission'
import { Grid } from './Grid'
import { Toolbar } from './Toolbar'
import { useApplyParticipantName } from './useApplyParticipantName'
import { useConnectionQualityReporter } from './useConnectionQuality'
import { useLowCPUOptimizer } from './usePerformanceOptimiser'
import styles from './medentee.module.css'

const COPIED_FEEDBACK_MS = 2000

const copyToClipboard = async (text: string) => {
  const written = await navigator.clipboard
    .writeText(text)
    .then(() => true)
    .catch(() => false)

  if (written) {
    return true
  }

  const field = document.createElement('textarea')

  field.value = text
  field.setAttribute('readonly', '')
  field.style.position = 'fixed'
  field.style.opacity = '0'
  document.body.appendChild(field)
  field.select()

  try {
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    field.remove()
  }
}

export type RoomProps = {
  onHangup: () => void

  hangupLabel?: string

  buttonClassName?: string

  joinUrl?: string

  onToggleFullscreen?: () => void
  isFullscreen?: boolean

  isCompact?: boolean

  displayName?: string

  onConnectionQualityChange?: (quality: ECallConnectionQuality) => void
}

export function Room({
  onHangup,
  hangupLabel,
  buttonClassName,
  joinUrl,
  onToggleFullscreen,
  isFullscreen,
  isCompact,
  displayName,
  onConnectionQualityChange
}: RoomProps) {
  useApplyParticipantName(displayName)
  useConnectionQualityReporter(onConnectionQualityChange)

  const participants = useParticipants()
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant()

  const cameraTracks = useTracks([Track.Source.Camera])

  const trackByIdentity = useMemo(
    () =>
      cameraTracks.reduce<Record<string, typeof cameraTracks[number]>>((acc, track) => {
        const identity = track.participant?.identity

        if (identity && track.publication && !track.publication.isMuted) {
          acc[identity] = track
        }

        return acc
      }, {}),
    [cameraTracks]
  )

  const visibleParticipants = useMemo(() => {
    const newestByIdentity = new Map<string, typeof participants[number]>()

    participants.forEach((participant) => {
      if (!participant.identity) {
        return
      }

      const known = newestByIdentity.get(participant.identity)
      const isNewer =
        !known || (participant.joinedAt?.getTime() ?? 0) >= (known.joinedAt?.getTime() ?? 0)

      if (isNewer) {
        newestByIdentity.set(participant.identity, participant)
      }
    })

    return Array.from(newestByIdentity.values())
  }, [participants])

  const room = useRoomContext()

  useLowCPUOptimizer(room)

  const [micWarning, setMicWarning] = useState<DeviceWarning>()
  const [cameraWarning, setCameraWarning] = useState<DeviceWarning>()
  const [blocked, setBlocked] = useState<BlockedDevice>()

  const micPermission = useDevicePermission('microphone')
  const cameraPermission = useDevicePermission('camera')

  useEffect(() => {
    const handleDeviceError = (error: Error, kind?: MediaDeviceKind) => {
      if (kind === 'audioinput') {
        setMicWarning(deviceWarning('microphone', error))
      }

      if (kind === 'videoinput') {
        setCameraWarning(deviceWarning('camera', error))
      }
    }

    room.on(RoomEvent.MediaDevicesError, handleDeviceError)

    return () => {
      room.off(RoomEvent.MediaDevicesError, handleDeviceError)
    }
  }, [room])

  const handleToggleMic = useCallback(async () => {
    if (
      micWarning?.reason === 'blocked' &&
      (await queryDevicePermission('microphone')) === 'denied'
    ) {
      setBlocked({ device: 'microphone', warning: micWarning })

      return
    }

    try {
      await localParticipant?.setMicrophoneEnabled(!isMicrophoneEnabled)
      setMicWarning(undefined)
      refreshDeviceList()
    } catch (error) {
      setMicWarning(deviceWarning('microphone', error))
    }
  }, [micWarning, localParticipant, isMicrophoneEnabled])

  const handleToggleCamera = useCallback(async () => {
    if (
      cameraWarning?.reason === 'blocked' &&
      (await queryDevicePermission('camera')) === 'denied'
    ) {
      setBlocked({ device: 'camera', warning: cameraWarning })

      return
    }

    try {
      await localParticipant?.setCameraEnabled(!isCameraEnabled)
      setCameraWarning(undefined)
      refreshDeviceList()
    } catch (error) {
      setCameraWarning(deviceWarning('camera', error))
    }
  }, [cameraWarning, localParticipant, isCameraEnabled])

  useEffect(() => {
    setMicWarning((current) => permissionWarning('microphone', micPermission, current))
  }, [micPermission])

  useEffect(() => {
    setCameraWarning((current) => permissionWarning('camera', cameraPermission, current))
  }, [cameraPermission])

  const [isLinkCopied, setIsLinkCopied] = useState(false)
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const stopCopiedTimer = useCallback(() => {
    if (copiedTimerRef.current) {
      clearTimeout(copiedTimerRef.current)
    }
  }, [])

  useEffect(() => stopCopiedTimer, [stopCopiedTimer])

  const handleCopyLink = useCallback(async () => {
    if (!joinUrl) {
      return
    }

    if (!(await copyToClipboard(joinUrl))) {
      return
    }

    setIsLinkCopied(true)
    stopCopiedTimer()
    copiedTimerRef.current = setTimeout(() => setIsLinkCopied(false), COPIED_FEEDBACK_MS)
  }, [joinUrl, stopCopiedTimer])

  return (
    <div className={styles.room}>
      {!!blocked && <DevicePermissionDialog {...blocked} onClose={() => setBlocked(undefined)} />}

      <RoomAudioRenderer />

      <Grid participants={visibleParticipants} trackByIdentity={trackByIdentity} />

      <Toolbar
        isMicOn={isMicrophoneEnabled}
        isCameraOn={isCameraEnabled}
        micWarning={micWarning}
        cameraWarning={cameraWarning}
        onToggleMic={handleToggleMic}
        onToggleCamera={handleToggleCamera}
        onHangup={onHangup}
        hangupLabel={hangupLabel}
        buttonClassName={buttonClassName}
        onCopyLink={joinUrl ? handleCopyLink : undefined}
        isLinkCopied={isLinkCopied}
        onToggleFullscreen={onToggleFullscreen}
        isFullscreen={isFullscreen}
        isCompact={isCompact}
      />
    </div>
  )
}
